import React, { useEffect, useState, useRef, useCallback } from "react";
import { RotateCw } from "lucide-react";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import "../styles/feedback.css";
import "../styles/chat.css";
import { getFeedbackList, sendFeedback } from "../services/feedbacks";
import { fetchMyRestrictions } from "../services/clientRestrictions";
import RestrictionNotice from "../components/RestrictionNotice";
import { formatRestrictionMessage, isModuleReadonly } from "../utils/clientRestrictionsUi";

const FeedbackClients = ({ highlightedFeedbacks = new Set(), setHighlightedFeedbacks = () => { }, hasClientAccess = false }) => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [statusMessage, setStatusMessage] = useState("");
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [forcedFeedbackId, setForcedFeedbackId] = useState(null);
    const [myRestriction, setMyRestriction] = useState(null);

    const canUseFeedbacks = hasClientAccess;
    const feedbacksReadonly = isModuleReadonly(myRestriction, "feedbacks");
    const textareaRef = useRef(null);
    const feedbackRefs = useRef(new Map());

    const fetchFeedbacks = useCallback(async () => {
        if (!canUseFeedbacks) {
            setStatusMessage("ℹ️ Відгуки доступні лише після прив'язки акаунта до локації/мережі.");
            setLoading(false);
            return;
        }

        const result = await getFeedbackList();
        if (result.success) {
            setFeedbacks(result.data);
        } else {
            setStatusMessage(`❌ ${result.error}`);
        }
        setLoading(false);
    }, [canUseFeedbacks]);

    useEffect(() => {
        fetchFeedbacks();
    }, [fetchFeedbacks]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchMyRestrictions();
                if (!cancelled) setMyRestriction(data);
            } catch (_) {
                if (!cancelled) setMyRestriction(null);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let raw = null;
        try {
            raw = localStorage.getItem("pendingFeedbackOpenId_client");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const id = parseInt(raw, 10);
        if (!Number.isFinite(id) || id <= 0) {
            try {
                localStorage.removeItem("pendingFeedbackOpenId_client");
            } catch (_) {
                // ignore
            }
            return;
        }

        const item = (feedbacks || []).find((fb) => Number(fb?.id) === id);
        if (!item) return;

        try {
            localStorage.removeItem("pendingFeedbackOpenId_client");
        } catch (_) {
            // ignore
        }

        setForcedFeedbackId(id);
        setHighlightedFeedbacks((prev) => new Set([...(prev || []), id]));

        const el = feedbackRefs.current.get(id);
        if (el) {
            window.requestAnimationFrame(() => {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        }

        const timer = window.setTimeout(() => {
            setForcedFeedbackId((prev) => (prev === id ? null : prev));
        }, 60000);

        return () => window.clearTimeout(timer);
    }, [feedbacks, setHighlightedFeedbacks]);

    // ✅ Filter feedbacks based on status and date range
    const getFilteredFeedbacks = () => {
        return feedbacks.filter((fb) => {
            // If date filter is applied, show closed feedbacks in the date range
            if (dateFrom || dateTo) {
                const feedbackDate = new Date(fb.created_at);
                const from = dateFrom ? new Date(dateFrom) : new Date("1900-01-01");
                const to = dateTo ? new Date(dateTo) : new Date("2099-12-31");

                // Set time to end of day for 'to' date
                to.setHours(23, 59, 59, 999);

                return feedbackDate >= from && feedbackDate <= to;
            }

            if (forcedFeedbackId && Number(fb?.id) === Number(forcedFeedbackId)) {
                return true;
            }

            // Default: show only 'new' and 'reviewing' feedbacks
            return fb.status === "new" || fb.status === "reviewing";
        });
    };

    const handleSend = async () => {
        if (!canUseFeedbacks) {
            setStatusMessage("ℹ️ Відгуки доступні лише після прив'язки акаунта до локації/мережі.");
            return;
        }
        if (feedbacksReadonly) {
            setStatusMessage(formatRestrictionMessage(myRestriction, "feedbacks"));
            return;
        }
        if (!newMessage.trim()) {
            alert("Введіть повідомлення перед відправкою");
            return;
        }

        const result = await sendFeedback({ message: newMessage });
        if (result.success) {
            setStatusMessage("✅ Відгук відправлено");
            setNewMessage("");
            fetchFeedbacks();
        } else {
            setStatusMessage(`❌ ${result.error}`);
        }
    };

    const handleTextareaResize = () => {
        const el = textareaRef.current;
        if (el) {
            el.style.height = "auto";
            el.style.height = `${el.scrollHeight}px`;
        }
    };

    if (loading)
        return (
            <div className="feedback-container">
                <p>Завантаження...</p>
            </div>
        );

    return (
        <div className="feedback-container">
            <h3>💬 Ваші відгуки</h3>

            {canUseFeedbacks && (
                <>
                    <div className="config-header">
                        <div className="header-buttons">
                            <button
                                className="add-btn refresh toolbar-icon-btn"
                                type="button"
                                onClick={fetchFeedbacks}
                                disabled={loading}
                                title="Оновити"
                                aria-label="Оновити"
                            >
                                <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                            </button>
                            <FilterPopover
                                active={Boolean(dateFrom || dateTo)}
                                title="Період відгуків"
                                hint="За замовчуванням показуються нові та ті, що на розгляді. Період показує закриті відгуки за датою."
                                buttonTitle="Фільтр за датою"
                                onClear={() => {
                                    setDateFrom("");
                                    setDateTo("");
                                }}
                            >
                                <DateRangeFields
                                    from={dateFrom}
                                    to={dateTo}
                                    onFromChange={setDateFrom}
                                    onToChange={setDateTo}
                                    fromLabel="Від дати"
                                    toLabel="До дати"
                                />
                            </FilterPopover>
                        </div>
                    </div>

                    <RestrictionNotice restriction={myRestriction} module="feedbacks" />

                    <div className="feedback-input-section">
                    <textarea
                        ref={textareaRef}
                        rows={3}
                        placeholder="Напишіть свій відгук..."
                        disabled={feedbacksReadonly}
                        value={newMessage}
                        onChange={(e) => {
                            setNewMessage(e.target.value);
                            handleTextareaResize();
                        }}
                    />
                    <button className="send-btn" onClick={handleSend} disabled={feedbacksReadonly}>
                        Надіслати
                    </button>
                </div>
                </>
            )}

            {statusMessage && <p className="status-msg">{statusMessage}</p>}

            {feedbacks.length === 0 ? (
                <p className="no-feedbacks">Відгуків ще немає</p>
            ) : getFilteredFeedbacks().length === 0 ? (
                <p className="no-feedbacks">Немає відгуків за вибраним періодом</p>
            ) : (
                <div className="feedback-list">
                    {[...getFilteredFeedbacks()]
                        .sort((a, b) => {
                            // Highlight feedbacks with recent admin responses come first
                            const aHighlighted = highlightedFeedbacks.has(a.id);
                            const bHighlighted = highlightedFeedbacks.has(b.id);
                            if (aHighlighted !== bHighlighted) {
                                return aHighlighted ? -1 : 1;
                            }
                            // Then sort by date (newest first)
                            return new Date(b.created_at) - new Date(a.created_at);
                        })
                        .map((fb) => (
                            <div
                                key={fb.id}
                                ref={(el) => {
                                    if (el) feedbackRefs.current.set(fb.id, el);
                                    else feedbackRefs.current.delete(fb.id);
                                }}
                                className={`feedback-card ${highlightedFeedbacks.has(fb.id) ? "highlighted" : ""}`}
                                style={forcedFeedbackId === fb.id ? {
                                    border: "2px solid #2563eb",
                                    boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.15)",
                                    background: "#f8fbff",
                                } : undefined}
                            >
                                <div className="feedback-header">
                                    {highlightedFeedbacks.has(fb.id) && (
                                        <span style={{ color: "#dc3545", marginRight: "4px", fontSize: "12px" }}>●</span>
                                    )}
                                    <span className={`status ${fb.status}`}>
                                        {fb.status === "new"
                                            ? "🔴 Новий"
                                            : fb.status === "reviewing"
                                                ? "🟡 Розглядається"
                                                : fb.status === "closed"
                                                    ? "🟢 Закритий"
                                                    : "⚪️ Невідомо"}
                                    </span>
                                    {highlightedFeedbacks.has(fb.id) && (
                                        <span style={{ color: "#dc3545", marginLeft: "auto", fontSize: "12px" }}>●</span>
                                    )}
                                    <span className="date">
                                        {new Date(fb.created_at).toLocaleString("uk-UA")}
                                    </span>
                                </div>

                                <div className="feedback-body">
                                    <p>
                                        <strong>Ваше повідомлення:</strong>
                                        <br />
                                        {fb.message}
                                    </p>
                                    <p className="response">
                                        <strong>Відповідь адміністратора:</strong>
                                        <br />
                                        {fb.response || "—"}
                                    </p>
                                </div>
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
};

export default FeedbackClients;
